'use client';
import React from 'react';
import Link from 'next/link';
import SearchBar from '@components/elements/SearchBar/SearchBar';
import { MagnifyingGlassIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { useFullScreenSearch } from '@contexts/fullscreensearch';
import { useRouter } from 'next/navigation';
import { toast } from 'react-toastify';
import { categoryDisplayName } from '@utils/category-name';
import { trackEvent } from '../../../lib/analytics/posthog';
import { EVENTS } from '../../../lib/analytics/events';

const CATEGORY_STYLES = {
  peluqueria: '✦',
  barberia: '◆',
  'centro-de-estetica': '❋',
  manicuria: '✧',
  'salon-de-belleza': '◇',
};

const DEFAULT_ICON = '✦';

const FALLBACK_CATEGORIES = [
  { keyword: 'Belleza', slug: 'belleza', icon: '✦' },
  { keyword: 'Maquillaje', slug: 'maquillaje', icon: '◆' },
  { keyword: 'Cejas', slug: 'cejas', icon: '❋' },
  { keyword: 'Pestañas', slug: 'pestanas', icon: '✧' },
  { keyword: 'Manos', slug: 'manos', icon: '◇' }
];

const Hero = ({ categories }) => {
  const { setShowSearchFullScreen } = useFullScreenSearch();
  const router = useRouter();

  const handleNearMe = () => {
    trackEvent(EVENTS.SEARCH_GPS_REQUESTED, { source: 'hero_cta' });
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          trackEvent(EVENTS.SEARCH_GPS_REQUESTED, { granted: true });
          router.push(`/negocios?lat=${position.coords.latitude}&lng=${position.coords.longitude}&distance=5`);
        },
        () => {
          trackEvent(EVENTS.SEARCH_GPS_REQUESTED, { granted: false });
          // Antes se iba a /negocios sin decir nada y parecía que «Cerca de mí» había funcionado.
          toast.info('No pudimos usar tu ubicación. Te mostramos negocios en Buenos Aires: podés elegir otra zona en el buscador.');
          router.push('/negocios');
        },
        { timeout: 8000 }
      );
    } else {
      router.push('/negocios');
    }
  };

  const pills = categories?.length > 0
    ? categories.slice(0, 5).map((c) => ({
        name: categoryDisplayName(c.keyword),
        slug: c.slug,
        icon: CATEGORY_STYLES[c.slug] || DEFAULT_ICON
      }))
    : FALLBACK_CATEGORIES.map((c) => ({
        name: c.keyword,
        slug: c.slug,
        icon: c.icon
      }));

  return (
    <div className="relative bg-gradient-to-b from-[#f0f4ff] via-[#f5f7fc] to-white">
      {/* Background decorative elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        {/* Primary gradient orb — top right */}
        <div
          className="absolute -top-[300px] -right-[200px] w-[700px] h-[700px] rounded-full animate-pulse-soft"
          style={{
            background: 'radial-gradient(circle, rgba(0,114,251,0.1) 0%, rgba(0,114,251,0.04) 40%, transparent 70%)'
          }}
        />
        {/* Warm accent orb — bottom left */}
        <div
          className="absolute -bottom-[200px] -left-[200px] w-[500px] h-[500px] rounded-full animate-pulse-soft delay-200"
          style={{
            background: 'radial-gradient(circle, rgba(255,70,88,0.06) 0%, transparent 70%)'
          }}
        />
        {/* Soft center glow */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] rounded-full opacity-40"
          style={{
            background: 'radial-gradient(ellipse, rgba(0,114,251,0.06) 0%, transparent 60%)'
          }}
        />
        {/* Subtle noise texture overlay */}
        <div
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
            backgroundSize: '128px 128px'
          }}
        />
      </div>

      <section id="hero" className="pt-16 min-h-[100svh] w-full relative">
        <div className="max-w-screen-lg mx-auto px-4 sm:px-6 flex flex-col justify-center w-full min-h-[calc(100svh-64px)] gap-6 md:gap-[clamp(20px,3.6svh,40px)] py-8">
          {/* Eyebrow text — glass badge */}
          <div className="animate-landing-fade-up">
            <span className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full text-[11px] font-semibold tracking-[0.15em] uppercase text-gray-500 glass-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-pulse" />
              Marketplace de belleza
            </span>
          </div>

          {/* Main headline */}
          <div className="animate-landing-fade-up delay-100">
            <h1 className="font-extrabold leading-[1.05] tracking-[-0.03em] text-[#111] text-[32px] sm:text-[42px] md:text-[clamp(40px,7.2svh,62px)]">
              <span className="block">Encontrá los mejores</span>
              <span className="block mt-1 md:mt-2">
                servicios de{' '}
                <span className="relative inline-block">
                  <span className="relative z-10">belleza</span>
                  <span
                    className="absolute bottom-[6px] md:bottom-[8px] left-0 w-full h-[8px] md:h-[10px] bg-gradient-to-r from-primary-300/70 to-primary-200/50 -z-0 rounded-full animate-landing-underline"
                  />
                </span>
              </span>
              <span className="block mt-1 md:mt-2 text-gray-500 font-semibold">
                cerca tuyo
              </span>
            </h1>
          </div>

          {/* Subtitle */}
          <div className="animate-landing-fade-up delay-200">
            <p className="text-gray-500 text-base md:text-lg max-w-md leading-relaxed font-medium">
              Explorá negocios de belleza, compará precios y reservá tu turno.
            </p>
          </div>

          {/* Search — Desktop: glass-wrapped */}
          <div className="w-full z-20 hidden md:block animate-landing-scale-in delay-300">
            <SearchBar isNavbar={false} />
          </div>

          {/* Search — Mobile: glass tap target */}
          <div className="w-full relative md:hidden animate-landing-scale-in delay-300">
            <button
              onClick={() => setShowSearchFullScreen(true)}
              className="flex items-center gap-3 w-full h-14 px-5 rounded-full glass-card hover:shadow-lg transition-all duration-300 text-left"
            >
              <MagnifyingGlassIcon className="h-[18px] w-[18px] text-gray-400 flex-shrink-0" />
              <span className="flex-1 text-[15px] text-gray-400 font-medium truncate">
                Servicio, categoría o negocio
              </span>
              <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0 shadow-sm shadow-primary-500/20">
                <MagnifyingGlassIcon className="h-4 w-4 text-white" />
              </div>
            </button>
          </div>

          {/* Popular categories — glass pills */}
          <div className="flex flex-col md:flex-row gap-3 items-start md:items-center w-full z-10 animate-landing-fade-up delay-400">
            <p className="text-[11px] text-gray-400 uppercase tracking-[0.15em] font-semibold whitespace-nowrap">Populares</p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleNearMe}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-xs rounded-full font-semibold text-white bg-primary-500 shadow-sm shadow-primary-500/20 hover:bg-primary-600 active:scale-[0.97] transition-all duration-200"
              >
                <MapPinIcon className="h-3.5 w-3.5" />
                Cerca de mí
              </button>
              {pills.map((item, index) => (
                <Link
                  className="group inline-flex items-center gap-2 px-4 py-2 text-xs rounded-full font-semibold text-gray-600 glass-badge hover:bg-white/70 hover:text-primary-600 transition-all duration-300"
                  href={`/${item.slug}`}
                  key={index}
                  onClick={() => trackEvent(EVENTS.SEARCH_CATEGORY_PILL_CLICKED, { category_name: item.name, category_slug: item.slug, source: 'hero' })}
                >
                  <span className="text-primary-400 group-hover:text-primary-500 transition-colors text-[10px]">
                    {item.icon}
                  </span>
                  {item.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom fade */}
        <div className="absolute bottom-0 left-0 w-full h-40 bg-gradient-to-t from-white via-white/60 to-transparent pointer-events-none" />
      </section>
    </div>
  );
};

export default Hero;
